/**
 * MissionModal — public-facing per-goal assessment timeline.
 *
 * DEV-1080, Chunk B. Opens when any viewer clicks the Mission shape on
 * the Mini-Project Progress tab. Status is the canonical record of
 * truth for per-goal assessments; this modal is a read-only time-series
 * view over filed statuses, NOT an independent edit surface.
 *
 * Composition (post-DEV-1075 Phase 7.3b):
 *   - Header: "Mission" + aggregate chip + close ×
 *   - Heatmap grid: rows = goals, columns = filed statuses (oldest →
 *     newest), each cell colored by that status's per-goal assessment.
 *     Empty cell = no assessment recorded for that (goal, date).
 *     Hover/title shows the date + assessment label.
 *   - Sticky goal-name column for projects with many filed statuses
 *     (horizontal scroll past modal width).
 *   - Empty / no-status state: friendly placeholder explaining where
 *     the cells come from.
 *   - Lead hint: "open Status update to change these" link to writer.
 *
 * Replaces the prior chip-strip layout (DEV-1080) where variable chip
 * widths caused date labels to drift out of alignment with their
 * corresponding columns and the design didn't scale past ~5 reporting
 * dates. The grid uses fixed column widths via CSS Grid so date
 * headers and data cells share the same track template — misalignment
 * is impossible by construction.
 *
 * Data path:
 *   The summary endpoint doesn't carry per-status goal_assessments (the
 *   array would inflate the calendar fetch, which loads on every page
 *   open). Instead the modal fetches the full status payload for each
 *   filed date on open, in parallel. Volumes are small (<= 200 statuses
 *   per project, capped server-side; typical projects will have a
 *   handful). If this becomes a perf concern, a dedicated
 *   /mission-timeline endpoint can replace the fan-out.
 *
 * Strings localized via `pages:miniProject.kpiModals.mission.*`
 * (Chunk D Phase 4 sweep, 2026-05-22).
 */

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchMiniProjectStatus,
  type MiniProjectStatus,
  type MiniProjectStatusSummary,
  type MiniProjectAssessment,
} from "../../api/miniProjectStatuses";
import type { MiniProjectGoal } from "../../api/miniProjects";
import {
  aggregateAssessmentsToColor,
  isQualitativeAssessment,
  type QualitativeAssessment,
} from "../../kpi-engine/qualitativeLadder";
import styles from "./MissionModal.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  miniProjectGuid: string;
  /** Active goals on the project (Goals section data). Provides display
   *  metadata — names, targets, units — that aren't in the status payload. */
  goals: MiniProjectGoal[];
  /** Status summaries (already loaded by ProgressTab — newest first).
   *  The modal fans out to fetch full payloads on open. */
  summaries: MiniProjectStatusSummary[];
  /** Drives the "open Status update" hint at the bottom. */
  isLead: boolean;
}

// Assessment → i18n key. Resolved at render via t() so labels localize.
const ASSESSMENT_LABEL_KEY: Record<MiniProjectAssessment, string> = {
  ahead: "miniProject.kpiModals.mission.assessment.ahead",
  on_track: "miniProject.kpiModals.mission.assessment.onTrack",
  behind: "miniProject.kpiModals.mission.assessment.behind",
  blocked: "miniProject.kpiModals.mission.assessment.blocked",
  done: "miniProject.kpiModals.mission.assessment.done",
};

/** Per-cell heatmap color class. Mirrors the existing chip palette
 *  (chip_success / chip_warning / chip_danger / chip_info) so the
 *  legend swatches and the cell fills share the exact same tokens.
 *  'done' is its own (info / blue) state — distinct from in-progress
 *  green — matching Skip's 2026-05-24 design decision. */
const ASSESSMENT_CELL_CLASS: Record<MiniProjectAssessment, string> = {
  ahead: "cellSuccess",
  on_track: "cellSuccess",
  behind: "cellWarning",
  blocked: "cellDanger",
  done: "cellInfo",
};

/* ── Helpers ───────────────────────────────────────────────────── */

function formatShortDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMonth(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, { month: "short" });
  } catch {
    return "";
  }
}

function formatDay(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return String(d || parseInt(iso.split("-")[2] || "0", 10));
  } catch {
    return iso;
  }
}

/** Mission aggregate at the latest status. Color comes from the
 *  shared 5-state ladder helper (qualitativeLadder.ts — consolidated
 *  in DEV-1075 Phase 7.3b from the inline copy this function
 *  previously maintained alongside StatusReader's). Counts drive the
 *  label; the ladder helper drives the color so the two surfaces
 *  (header aggregate + per-cell) can never drift. */
function aggregateLatest(
  full: MiniProjectStatus | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { color: "info" | "success" | "warning" | "danger" | "muted"; label: string } {
  if (!full || full.goalAssessments.length === 0)
    return { color: "muted", label: t("miniProject.kpiModals.mission.aggregate.noGoals") };

  const valid: QualitativeAssessment[] = full.goalAssessments
    .map((a) => a.assessment)
    .filter(isQualitativeAssessment);

  const { colorValue, allDone } = aggregateAssessmentsToColor(valid);

  const counts = { ahead: 0, on_track: 0, behind: 0, blocked: 0, done: 0 };
  for (const a of valid) counts[a]++;

  if (colorValue === 1)
    return { color: "danger", label: t("miniProject.kpiModals.mission.aggregate.blocked", { count: counts.blocked }) };
  if (colorValue === 2)
    return { color: "warning", label: t("miniProject.kpiModals.mission.aggregate.behind", { count: counts.behind }) };
  if (colorValue === 3 && allDone)
    return { color: "info", label: t("miniProject.kpiModals.mission.aggregate.allComplete") };
  if (colorValue === 3)
    return { color: "success", label: t("miniProject.kpiModals.mission.aggregate.inProgress", { count: counts.ahead + counts.on_track }) };
  return { color: "muted", label: t("miniProject.kpiModals.mission.aggregate.noGoals") };
}

/* ── Component ─────────────────────────────────────────────────── */

const MissionModal: React.FC<Props> = ({
  open,
  onClose,
  miniProjectGuid,
  goals,
  summaries,
  isLead,
}) => {
  const { t } = useAppTranslation("pages");
  // Full status fetches — on open, fan out via Promise.all.
  const [fullStatuses, setFullStatuses] = useState<MiniProjectStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Escape key close.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // On open: load full statuses. Reset when the modal closes so the
  // next open re-fetches fresh data (cheap, and avoids stale cells
  // if the Lead edited a status in between opens).
  useEffect(() => {
    if (!open) {
      setFullStatuses([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(
      summaries.map((s) =>
        fetchMiniProjectStatus(miniProjectGuid, s.statusGuid),
      ),
    )
      .then((statuses) => {
        if (cancelled) return;
        setFullStatuses(statuses);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, miniProjectGuid, summaries]);

  // Chronological order — oldest left, newest right. Server returns
  // newest-first, so reverse here for the heatmap rendering.
  const chrono = useMemo(() => [...fullStatuses].reverse(), [fullStatuses]);

  // Per (goal, status) assessment lookup. The heatmap renders one cell
  // per (goal, status) pair so we need O(1) lookup by both axes. A flat
  // Map keyed by `"<goalGuidUpper>::<statusGuid>"` is dense and simple.
  // Goals deleted between filings are silently skipped (we only render
  // rows for goals that currently exist on the project).
  const cellByGoalAndStatus = useMemo(() => {
    const m = new Map<string, MiniProjectAssessment>();
    const activeGoalGuids = new Set(goals.map((g) => g.goalGuid.toUpperCase()));
    for (const s of chrono) {
      for (const a of s.goalAssessments) {
        const key = a.goalGuid.toUpperCase();
        if (!activeGoalGuids.has(key)) continue;
        m.set(`${key}::${s.statusGuid}`, a.assessment);
      }
    }
    return m;
  }, [goals, chrono]);

  // Aggregate uses the LATEST full status (newest reportingDate).
  const latestFull = fullStatuses[0] || null;
  const agg = aggregateLatest(latestFull, t);

  if (!open) return null;

  // CSS Grid column template: name column + N data columns. Variable
  // count drives the columns via the inline custom property so the
  // CSS file doesn't need to know how many statuses exist.
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: `var(--mm-name-col, 180px) repeat(${chrono.length}, var(--mm-cell, 28px))`,
  };

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("miniProject.kpiModals.mission.title")}>
        {/* ── Header ── */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.title}>{t("miniProject.kpiModals.mission.title")}</span>
            <span className={`${styles.aggChip} ${styles[`chip_${agg.color}`]}`}>
              <span className={styles.aggDot} aria-hidden="true" />
              {agg.label}
            </span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("miniProject.kpiModals.mission.close")}>
            ×
          </button>
        </header>

        {/* ── Body ── */}
        <div className={styles.body}>
          {loading && fullStatuses.length === 0 && (
            <div className={styles.emptyState}>{t("miniProject.kpiModals.mission.loading")}</div>
          )}
          {error && (
            <div className={styles.errorMsg}>{error}</div>
          )}

          {!loading && !error && goals.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{t("miniProject.kpiModals.mission.empty.noGoalsTitle")}</p>
              <p className={styles.emptyHint}>
                {isLead
                  ? t("miniProject.kpiModals.mission.empty.noGoalsLeadHint")
                  : t("miniProject.kpiModals.mission.empty.noGoalsPublicHint")}
              </p>
            </div>
          )}

          {!loading && !error && goals.length > 0 && summaries.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{t("miniProject.kpiModals.mission.empty.noStatusTitle")}</p>
              <p className={styles.emptyHint}>
                {isLead
                  ? t("miniProject.kpiModals.mission.empty.noStatusLeadHint")
                  : t("miniProject.kpiModals.mission.empty.noStatusPublicHint")}
              </p>
            </div>
          )}

          {!loading && !error && goals.length > 0 && summaries.length > 0 && (
            <>
              {/* Heatmap: header row + per-goal rows in a single CSS Grid.
                  Date headers share column tracks with data cells, so
                  alignment is structural rather than approximate. */}
              <div className={styles.heatmapFrame}>
                <div className={styles.heatmap} style={gridStyle}>
                  {/* Top-left corner cell — empty, sits above the
                      goal-name column. Sticky-positioned so it scrolls
                      with the name column under it. */}
                  <div className={`${styles.cornerCell} ${styles.stickyLeft}`} />

                  {/* Date header row. */}
                  {chrono.map((s) => (
                    <div key={`d-${s.statusGuid}`} className={styles.dateCell}>
                      <span className={styles.dateMonth}>{formatMonth(s.reportingDate)}</span>
                      <span className={styles.dateDay}>{formatDay(s.reportingDate)}</span>
                    </div>
                  ))}

                  {/* Per-goal rows. */}
                  {goals.map((g) => {
                    const currentValue = Number(g.currentValue || 0);
                    const targetValue = g.targetValue ? Number(g.targetValue) : null;
                    const goalKey = g.goalGuid.toUpperCase();
                    return (
                      <React.Fragment key={g.goalGuid}>
                        <div className={`${styles.nameCell} ${styles.stickyLeft}`}>
                          <div className={styles.nameMain}>{g.nameKey}</div>
                          <div className={styles.nameProgress}>
                            {targetValue !== null
                              ? t("miniProject.kpiModals.mission.goal.progressOf", {
                                  current: currentValue,
                                  target: targetValue,
                                  unit: g.unitKey || "",
                                })
                              : t("miniProject.kpiModals.mission.goal.progressNoTarget", {
                                  current: currentValue,
                                  unit: g.unitKey || "",
                                })}
                          </div>
                        </div>
                        {chrono.map((s) => {
                          const assessment = cellByGoalAndStatus.get(`${goalKey}::${s.statusGuid}`);
                          if (!assessment) {
                            return (
                              <div
                                key={`${g.goalGuid}-${s.statusGuid}`}
                                className={styles.cellEmpty}
                                title={t("miniProject.kpiModals.mission.cellEmptyTitle", {
                                  date: formatShortDate(s.reportingDate),
                                })}
                              />
                            );
                          }
                          const assessmentLabel = t(ASSESSMENT_LABEL_KEY[assessment]);
                          return (
                            <div
                              key={`${g.goalGuid}-${s.statusGuid}`}
                              className={`${styles.cell} ${styles[ASSESSMENT_CELL_CLASS[assessment]]}`}
                              title={t("miniProject.kpiModals.mission.chipTitle", {
                                date: formatShortDate(s.reportingDate),
                                assessment: assessmentLabel,
                              })}
                              aria-label={t("miniProject.kpiModals.mission.chipAria", {
                                date: s.reportingDate,
                                assessment: assessmentLabel,
                              })}
                            />
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.cellSuccess}`} />
                  {t("miniProject.kpiModals.mission.legend.aheadOnTrack")}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.cellWarning}`} />
                  {t("miniProject.kpiModals.mission.legend.behind")}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.cellDanger}`} />
                  {t("miniProject.kpiModals.mission.legend.blocked")}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.cellInfo}`} />
                  {t("miniProject.kpiModals.mission.legend.done")}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.cellEmpty}`} />
                  {t("miniProject.kpiModals.mission.legend.noAssessment")}
                </span>
              </div>

              {isLead && (
                <div className={styles.leadHint}>
                  {t("miniProject.kpiModals.mission.leadHint")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MissionModal;
